/**
 * vs-cart-sync.js
 *
 * Shared helpers that let the custom "vs-*" sections dispatch cart updates
 * in the exact shape Horizon's native cart components expect.
 *
 * Horizon's component-cart-items.js reads `event.detail.data.sections[sectionId]`.
 * The earlier custom code dispatched `cart:update` with no `data` key at all,
 * so the native handler threw `Cannot read properties of undefined (reading 'sections')`
 * and the cart drawer DOM never morphed to show the added item.
 *
 * This helper:
 *   1. Builds the `sections` query string from drawer / cart-icon / cart-page sections
 *      currently in the DOM, so the POST /cart/add.js response includes their HTML.
 *   2. Attaches `sections` + `sections_url` to either a JSON body object or a FormData.
 *   3. Dispatches `cart:update` with the full detail shape Horizon expects.
 *   4. Opens the cart drawer, picking the modern `cart-drawer-component` first.
 *
 * It is idempotent and safe to load more than once.
 */
(function () {
  'use strict';

  if (window.VsCartSync) return;

  var DRAWER_SELECTORS = 'cart-drawer-component, cart-drawer, cart-icon, cart-items-component, [data-cart-bubble]';

  function getSectionIds() {
    var ids = [];
    document.querySelectorAll(DRAWER_SELECTORS).forEach(function (el) {
      var sectionEl = el.closest('[id^="shopify-section-"]');
      if (!sectionEl) return;
      var id = sectionEl.id.replace('shopify-section-', '');
      if (ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function getSectionsParam() {
    return getSectionIds().join(',');
  }

  function attachSectionsToJson(body) {
    var sections = getSectionsParam();
    if (!sections) return body;
    body.sections = sections;
    body.sections_url = window.location.pathname;
    return body;
  }

  function attachSectionsToForm(formData) {
    var sections = getSectionsParam();
    if (!sections) return formData;
    formData.append('sections', sections);
    formData.append('sections_url', window.location.pathname);
    return formData;
  }

  /**
   * Dispatch `cart:update` so Horizon's native cart-items component morphs its DOM.
   *
   * @param {Object} opts
   * @param {Object} opts.cart        - Parsed /cart.js response (has item_count, items, etc.)
   * @param {Object} [opts.addResponse] - Parsed /cart/add.js response. If it carries
   *                                      `sections`, those are passed through so Horizon
   *                                      morphs the drawer with that HTML directly.
   * @param {string} [opts.source]    - Short source identifier for debugging.
   */
  function notify(opts) {
    opts = opts || {};
    var cart = opts.cart || {};
    var addResponse = opts.addResponse || {};
    var source = opts.source || 'vs-cart-sync';

    var sections = addResponse.sections && typeof addResponse.sections === 'object'
      ? addResponse.sections
      : {};

    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        source: source,
        sourceId: source,
        data: {
          source: source,
          itemCount: cart.item_count,
          sections: sections
        }
      }
    }));
  }

  function openDrawer() {
    var drawer = document.querySelector('cart-drawer-component, cart-drawer');
    if (!drawer) return;
    if (typeof drawer.showDialog === 'function') {
      drawer.showDialog();
    } else if (typeof drawer.open === 'function') {
      drawer.open();
    }
  }

  window.VsCartSync = {
    getSectionsParam: getSectionsParam,
    attachSectionsToJson: attachSectionsToJson,
    attachSectionsToForm: attachSectionsToForm,
    notify: notify,
    openDrawer: openDrawer
  };
})();
